import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { BASE_URL } from "../Constants/common";
import { isLoginRequestUrl } from "../Constants/apiPaths";
import {
  NETWORK_ERROR_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
  SOMETHING_WENT_WRONG,
} from "../Constants/messages";
import { getAccessToken } from "../Helper/user.helper";
import { setErrorMessage } from "../Redux/Slices/alert.slice";
import store from "../Redux/store";
import { forceLogout } from "../Services/auth/session.service";

const REQUEST_TIMEOUT_IN_MS = 45000;
const CANCELED_REQUEST_CODE = "ERR_CANCELED";

interface ApiErrorResponse {
  status?: string;
  statusCode?: number;
  message?: string;
  responseData?: unknown;
  time?: string;
}

const attachAuthorizationHeader = (config: InternalAxiosRequestConfig) => {
  const accessToken = getAccessToken();

  if (accessToken && !isLoginRequestUrl(config.url)) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
};

const handleResponseError = (error: AxiosError<ApiErrorResponse>) => {
  if (error.code === CANCELED_REQUEST_CODE) {
    return Promise.reject(error);
  }

  const status = error.response?.status;
  const apiMessage = error.response?.data?.message ?? null;

  // Credential errors belong to the login form, which renders them inline.
  if (isLoginRequestUrl(error.config?.url)) {
    return Promise.reject(error);
  }

  if (status === 401) {
    forceLogout({ message: apiMessage ?? SESSION_EXPIRED_MESSAGE });
    return Promise.reject(error);
  }

  if (!error.response) {
    store.dispatch(setErrorMessage(NETWORK_ERROR_MESSAGE));
    return Promise.reject(error);
  }

  store.dispatch(setErrorMessage(apiMessage ?? SOMETHING_WENT_WRONG));
  return Promise.reject(error);
};

export const configureAxios = () => {
  axios.defaults.baseURL = BASE_URL;
  axios.defaults.timeout = REQUEST_TIMEOUT_IN_MS;
  axios.defaults.withCredentials = true;

  axios.interceptors.request.use(attachAuthorizationHeader);
  axios.interceptors.response.use(undefined, handleResponseError);
};
